import { useCallback, useEffect, useMemo } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import type { Dispatch } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useLocation, withRouter } from 'react-router';
import type { FormApi } from 'final-form';

import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { Button } from 'UIPackage/MainModule/components/Button';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { INotification } from 'CorePackage/NotificationModule/interfaces/INotification';
import { Notification } from 'CorePackage/NotificationModule/components/Notification';
import { ORGANIZATION } from 'CorePackage/MainModule/constants/localStorageNames';
import { CODE, STATE } from 'CorePackage/MainModule/constants/auth';
import { IUserOrganization } from 'APIPackage/AuthModule/interfaces/IUserOrganization';
import { ROUTE_AUTH } from 'CorePackage/MainModule/routes/routes';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IResponseError } from 'APIPackage/MainModule/interfaces/IResponseError';

import { authInitEsiaAuthProcess, authLogin, authSetError, authSetStage } from '../actions/authActions';
import { OrganizationTable } from './OrganizationTable';
import styles from './UserLogin.module.scss';
import { onSelectOrganization } from '../common/onSelectOrganization';
import { IAuthState } from '../interfaces/IAuthState';
import { getAuthError, getAuthStage, getAuthOrganizations } from '../selectors/authSelectors';

interface IUserLoginForm {
  username: string;
  password: string;
}

const initialValues: IUserLoginForm = {
  username: '',
  password: '',
};

interface IUserLoginStoreProps {
  error?: IResponseError;
  stage: IAuthState['stage'];
  organizations: IUserOrganization[] | undefined;
}

const UserLoginWithoutStore = ({ error, organizations, stage }: IUserLoginStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const location = useLocation();
  const history = useHistory();

  const handleEsiaLogin = useCallback(() => {
    const href = '/esia-authentication-provider/api/esiaauth/login';

    window.location.href = window.location.origin + href;
  }, []);

  const goBack = useCallback(() => {
    sessionStorage.removeItem(ORGANIZATION);
    history.push(ROUTE_AUTH);
    dispatch(authSetStage('login'));
  }, [dispatch, history]);

  const handleFormSubmit = useCallback(
    (values: IUserLoginForm, form: FormApi<IUserLoginForm>) => {
      dispatch(authLogin(values.username, values.password, history));
      form.change('password', '');
    },
    [dispatch, history],
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    if (!!searchParams.get(STATE) && !!searchParams.get(CODE)) {
      dispatch(authInitEsiaAuthProcess(history));
    }
  }, [dispatch, history, location]);

  const errorInfo = useMemo(() => {
    if (error === undefined) {
      return null;
    }

    function clearError() {
      dispatch(authSetError(undefined));
    }

    if (error?.status && error.status >= 500) {
      const notice: INotification = {
        renderContent: 'Ошибка. Попробуйте повторить позднее.',
        noticeType: NoticeTypesEnum.ERROR,
        messageType: MessageTypesEnum.AUTHORIZATION,
      };

      return <Notification notice={notice} close={clearError} />;
    }

    const notice: INotification = {
      renderContent:
        'Авторизация не пройдена. Убедитесь в корректности введенных данных. Если вы используете для авторизации ЕСИА, то нажмите "Войти через ЕСИА".',
      noticeType: NoticeTypesEnum.ERROR,
      messageType: MessageTypesEnum.AUTHORIZATION,
    };

    return <Notification notice={notice} close={clearError} />;
  }, [dispatch, error]);

  const renderForm = useCallback(
    ({ handleSubmit }: FormRenderProps<IUserLoginForm>) => (
      <form onSubmit={handleSubmit}>
        <Row>
          <FieldBlock>
            <TextInputField
              name="username"
              className="shared-field_margin-bottom"
              placeholder="Логин"
              validate={makeRequired('Поле обязательно')}
              tooltip="Логин не может быть пустым"
              disableErrorStyles
              isFocused
            />
            <TextInputField
              name="password"
              type="password"
              placeholder="Пароль"
              tooltip="Пароль не может быть пустым"
              disableErrorStyles
              validate={makeRequired('Поле обязательно')}
            />
          </FieldBlock>
        </Row>
        <ModalActions direction="column" align="centre">
          <Button color="primary" text="Войти" type="submit" />
          <Button text="Войти через ЕСИА" onClick={handleEsiaLogin} type="button" variant="linkButton" />
        </ModalActions>
      </form>
    ),
    [handleEsiaLogin],
  );

  return stage === 'login' ? (
    <div className={styles.Wrapper}>
      <ModalHeader title="Вход в систему" color="black" size="large" placement="centre" />
      {errorInfo}
      <Form<IUserLoginForm>
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        render={renderForm}
        subscription={{}}
      />
    </div>
  ) : (
    <>
      <Row>
        <FieldBlock
          label="Список доступных для входа организаций"
          color="black"
          labelFontSize="medium"
          button={<Button color="primary" text="Отмена" onClick={goBack} />}
        />
      </Row>
      <OrganizationTable data={organizations} onEnter={onSelectOrganization} />
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  error: getAuthError(state),
  stage: getAuthStage(state),
  organizations: getAuthOrganizations(state),
});

export const UserLogin = withRouter(connect(mapStateToProps)(UserLoginWithoutStore));
